import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Button, Table, Space, Breadcrumb, Empty, Spin,
  message, Popconfirm, Tooltip, Typography, Row, Col,
} from 'antd';
import {
  HomeOutlined, ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, FilePdfOutlined,
} from '@ant-design/icons';
import SmilepassportPreviewModal from '../components/SmilepassportPreviewModal';
import { smilepassportApi } from '../../../api/smilepassport.api';
import { eventApi } from '../../../api/event.api';
import { registrationApi } from '../../../api/registration.api';
import { formatDate } from '../../../utils/helpers';
import SmilepassportModal from '../components/SmilepassportModal';
import { usePermission } from '../../../hooks/usePermission';

const { Title, Text } = Typography;

const SmilepassportList = () => {
  const { eventId, childId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = usePermission();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);

  // Fetch event data
  const { data: eventData, isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventApi.getById(eventId),
  });

  // Fetch registration to get child info
  const { data: regData, isLoading: regLoading } = useQuery({
    queryKey: ['registration-by-child-event', childId, eventId],
    queryFn: () => registrationApi.getByEvent(eventId),
    select: (data) => {
      const registrations = data?.data || [];
      return registrations.find((r) => {
        const cid = r.child?.id || r.child?._id || String(r.child || '');
        return cid === childId;
      });
    },
  });

  // Fetch smilepassport record for this child in this event
  const { data: smilepassportsData, isLoading: smilepassportsLoading } = useQuery({
    queryKey: ['smilepassports-by-event-child', eventId, childId],
    queryFn: () => childId ? smilepassportApi.getByEventAndChild(eventId, childId) : Promise.resolve([]),
    enabled: !!childId,
    select: (data) => data?.data || [],
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => smilepassportApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['smilepassports-by-event-child', eventId, childId]);
      message.success('Smilepassport record deleted successfully');
    },
    onError: (err) => message.error(err.message || 'Failed to delete record'),
  });

  const child = regData?.child;
  const records = smilepassportsData || [];

  if (eventLoading || regLoading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  if (!child) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Text type="secondary">Child not found.</Text>
        <br />
        <Button style={{ marginTop: 12 }} onClick={() => navigate(`/events/${eventId}`)}>
          Back to Event
        </Button>
      </div>
    );
  }

  const event = eventData?.data;

  const truncate = (text) => {
    if (!text) return <Text style={{ fontSize: 12 }}>—</Text>;
    if (text.length <= 10) return <Text style={{ fontSize: 12 }}>{text}</Text>;
    return (
      <Tooltip title={text}>
        <Text style={{ fontSize: 12 }}>{text.slice(0, 10)}...</Text>
      </Tooltip>
    );
  };

  const STATUS_STYLE = {
    published: { color: '#52c41a', fontWeight: 700 },
    draft:     { color: '#fa8c16', fontWeight: 700 },
  };

  // Table columns
  const columns = [
    {
      title: '#',
      width: 44,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>,
    },
    {
      title: 'Academic Year',
      width: 140,
      render: (_, r) => <Text style={{ fontSize: 12 }}>{r.academic_year || '—'}</Text>,
    },
    {
      title: 'Created Date',
      width: 140,
      render: (_, r) => <Text style={{ fontSize: 12 }}>{formatDate(r.createdAt || r.recordedAt)}</Text>,
    },
    {
      title: 'Concern',
      render: (_, r) => truncate(r.concern),
    },
    {
      title: 'Updated',
      width: 140,
      render: (_, r) => <Text type="secondary" style={{ fontSize: 11 }}>{formatDate(r.updatedAt)}</Text>,
    },
    {
      title: 'Status',
      width: 100,
      render: (_, r) => {
        const s = r.status || 'draft';
        return (
          <span style={{ ...(STATUS_STYLE[s] || STATUS_STYLE.draft), textTransform: 'capitalize', fontSize: 13 }}>
            {s}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      width: 140,
      align: 'center',
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="Preview PDF">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => {
                setPreviewRecord(r);
                setPreviewOpen(true);
              }}
            />
          </Tooltip>
          {isAdmin && r.pdf_path && (
            <Tooltip title="Open saved PDF">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => window.open(r.pdf_path, '_blank')}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined />}
              style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
              onClick={() => {
                setEditingRecord(r);
                setModalOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => deleteMutation.mutate(r.id || r._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            placement="topLeft"
          >
            <Button shape="circle" danger size="small" icon={<DeleteOutlined />} style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: <span onClick={() => navigate('/events')} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>Events</span> },
          { title: <span onClick={() => navigate(`/events/${eventId}`)} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>{event?.title}</span> },
          { title: `${child?.name} - Smile Passport` },
        ]}
      />

      {/* Header Card */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid var(--wl-primary-pale)',
          boxShadow: '0 2px 16px rgba(var(--wl-primary-rgb),0.09)',
          marginBottom: 20,
        }}
        styles={{ body: { padding: 24 } }}
      >
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={16}>
            <Space align="center" style={{ marginBottom: 12 }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(`/events/${eventId}`)}
                style={{ marginLeft: -8 }}
              />
              <div>
                <Title level={3} style={{ margin: 0, color: 'var(--wl-text)' }}>
                  {child?.name}
                </Title>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Smile Passport Records
                </Text>
              </div>
            </Space>
            <Space size={16} style={{ marginTop: 12 }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Age:</Text>
                <Text strong style={{ marginLeft: 8 }}>{child?.age} years</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Gender:</Text>
                <Text strong style={{ marginLeft: 8, textTransform: 'capitalize' }}>{child?.gender}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Parent:</Text>
                <Text strong style={{ marginLeft: 8 }}>{child?.parent?.name}</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            <Space direction="vertical" align="end" style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Records</Text>
                <div style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: 'var(--wl-primary)',
                  marginTop: 4,
                }}>
                  {records.length}
                </div>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingRecord(null);
                  setModalOpen(true);
                }}
                style={{
                  marginTop: 8,
                  background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))',
                  border: 'none',
                }}
              >
                Add New Record
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Records Table */}
      <Card
        style={{ borderRadius: 12, border: '1px solid var(--wl-primary-pale)' }}
        styles={{ body: { padding: 0 } }}
      >
        {records.length === 0 ? (
          <Empty
            description="No Smile Passport records found"
            style={{ padding: '60px 20px' }}
          />
        ) : (
          <Table
            dataSource={records}
            columns={columns}
            rowKey={(r) => r.id || r._id}
            loading={smilepassportsLoading}
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `${total} records`,
            }}
            scroll={{ x: 800 }}
            style={{ padding: '0 4px' }}
          />
        )}
      </Card>

      {/* Smilepassport Modal */}
      <SmilepassportModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingRecord(null);
        }}
        eventId={eventId}
        childId={childId}
        childName={child?.name}
        editingRecord={editingRecord}
        onSuccess={() => {
          queryClient.invalidateQueries(['smilepassports-by-event-child', eventId, childId]);
          setModalOpen(false);
          setEditingRecord(null);
        }}
      />
      <SmilepassportPreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewRecord(null);
        }}
        record={previewRecord}
        child={child}
      />
    </div>
  );
};

export default SmilepassportList;
